import { useState } from "react";
import { Link } from "react-router-dom";
import { Minus, Plus, X, ShoppingBag, Lock, AlertTriangle } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useCart } from "@/context/CartContext";
import SaveCartForm from "@/components/SaveCartForm";
import { trackEvent } from "@/lib/analytics";
import { CART } from "@/constants/testIds";

function money(n) {
  return `$${n.toFixed(2)}`;
}

export default function CartDrawer() {
  const {
    items, removeItem, updateQuantity, subtotal, count, open, setOpen, checkout, checkingOut, checkoutError,
    discountCode, discountInfo, discountLoading, discountAmount, total, applyDiscount, removeDiscount,
  } = useCart();
  const [discountInput, setDiscountInput] = useState("");

  const handleApplyDiscount = async () => {
    const result = await applyDiscount(discountInput);
    if (result?.ok) setDiscountInput("");
  };

  const handleRemove = (item) => {
    removeItem(item.id);
    trackEvent("shop_remove_from_cart", { product_name: item.name, quantity: item.quantity, cart_value: subtotal });
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent side="right" className="bg-[#0a0a0a] border-white/10 text-white w-full sm:max-w-md flex flex-col p-0">
        <SheetHeader className="p-6 border-b border-white/10">
          <SheetTitle className="text-white font-display uppercase tracking-tight flex items-center gap-2">
            <ShoppingBag size={18} className="text-[#FF6B00]" />
            Your Cart {count > 0 && <span className="text-[#FF6B00]">({count})</span>}
          </SheetTitle>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto p-6 space-y-4" data-testid="cart-items">
          {items.length === 0 ? (
            <div className="text-center py-16" data-testid="cart-empty-state">
              <ShoppingBag size={32} className="mx-auto text-white/20 mb-4" />
              <p className="text-white/50 text-sm">Your cart is empty.</p>
            </div>
          ) : (
            items.map((item) => (
              <div key={item.id} data-testid={`cart-item-${item.id}`} className="flex gap-4 border border-white/10 rounded-2xl p-3">
                <div className="w-16 h-16 rounded-xl overflow-hidden bg-[#111] shrink-0">
                  <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <h4 className="font-bold text-sm leading-tight">{item.name}</h4>
                    <button
                      type="button"
                      data-testid={`cart-item-remove-${item.id}`}
                      onClick={() => handleRemove(item)}
                      className="text-white/40 hover:text-white shrink-0"
                      aria-label={`Remove ${item.name} from cart`}
                    >
                      <X size={14} />
                    </button>
                  </div>
                  {item.variantNote && <p className="text-xs text-white/50 mt-0.5">{item.variantNote}</p>}
                  <div className="mt-2 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        data-testid={`cart-item-decrease-${item.id}`}
                        onClick={() => updateQuantity(item.id, item.quantity - 1)}
                        disabled={item.quantity <= 1}
                        aria-label="Decrease quantity"
                        className="w-6 h-6 rounded-md border border-white/15 disabled:opacity-30 flex items-center justify-center"
                      >
                        <Minus size={10} />
                      </button>
                      <span className="text-sm font-bold w-4 text-center">{item.quantity}</span>
                      <button
                        type="button"
                        data-testid={`cart-item-increase-${item.id}`}
                        onClick={() => updateQuantity(item.id, item.quantity + 1)}
                        aria-label="Increase quantity"
                        className="w-6 h-6 rounded-md border border-white/15 flex items-center justify-center"
                      >
                        <Plus size={10} />
                      </button>
                    </div>
                    <span className="text-sm font-bold text-[#FF6B00]">{money(item.unitPrice * item.quantity)}</span>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {items.length > 0 && (
          <div className="p-6 border-t border-white/10 space-y-3">
            {!discountCode ? (
              <div className="flex gap-2">
                <input
                  type="text"
                  data-testid={CART.discountInput}
                  value={discountInput}
                  onChange={(e) => setDiscountInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleApplyDiscount()}
                  placeholder="Discount code"
                  className="flex-1 bg-black/40 border border-white/15 rounded-lg px-3 py-2 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[#FF6B00]"
                />
                <button
                  type="button"
                  data-testid={CART.discountApply}
                  onClick={handleApplyDiscount}
                  disabled={discountLoading || !discountInput.trim()}
                  className="px-4 py-2 rounded-lg border border-white/20 text-xs font-bold uppercase tracking-wider hover:border-[#FF6B00] disabled:opacity-50 transition-colors"
                >
                  {discountLoading ? "..." : "Apply"}
                </button>
              </div>
            ) : (
              <div className="flex items-center justify-between bg-[#FF6B00]/10 border border-[#FF6B00]/30 rounded-lg px-3 py-2">
                <span data-testid={CART.discountActive} className="text-xs font-bold text-[#FF6B00]">
                  {discountCode} applied — -{money(discountAmount)}
                </span>
                <button
                  type="button"
                  data-testid={CART.discountRemove}
                  onClick={removeDiscount}
                  className="text-white/50 hover:text-white text-xs font-bold uppercase tracking-wider"
                >
                  Remove
                </button>
              </div>
            )}

            <div className="flex items-center justify-between text-sm">
              <span className="text-white/60">Subtotal</span>
              <span data-testid="cart-subtotal" className="font-bold">{money(subtotal)}</span>
            </div>
            {discountCode && (
              <div className="flex items-center justify-between text-sm">
                <span className="text-white/60">Discount ({discountCode})</span>
                <span data-testid={CART.discountAmount} className="font-bold text-[#FF6B00]">-{money(discountAmount)}</span>
              </div>
            )}
            <div className="flex items-center justify-between pt-1 border-t border-white/10">
              <span className="text-sm text-white/60">Estimated Total</span>
              <span data-testid={CART.total} className="font-display text-xl font-black">{money(total)}</span>
            </div>
            <p className="text-xs text-white/40">Final tax and shipping are calculated at checkout.</p>
            {checkoutError && (
              <div data-testid="cart-checkout-error" className="rounded-lg border border-red-500/30 bg-red-500/10 p-3 space-y-2">
                <div className="flex items-start gap-2 text-sm text-red-300">
                  <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                  <span>{checkoutError} Your cart has been saved — nothing was lost.</span>
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    data-testid="cart-checkout-retry"
                    onClick={checkout}
                    className="flex-1 bg-white/10 hover:bg-white/20 text-white font-bold uppercase tracking-wider text-xs px-4 py-2 rounded-md transition-colors"
                  >
                    Retry
                  </button>
                  <Link
                    to="/contact"
                    data-testid="cart-checkout-contact-support"
                    className="flex-1 text-center border border-white/20 hover:border-white/40 text-white font-bold uppercase tracking-wider text-xs px-4 py-2 rounded-md transition-colors"
                  >
                    Contact Support
                  </Link>
                </div>
              </div>
            )}
            <button
              type="button"
              data-testid="cart-checkout-button"
              onClick={checkout}
              disabled={checkingOut}
              className="w-full bg-[#FF6B00] hover:bg-[#E66000] disabled:opacity-50 text-black font-bold uppercase tracking-wider text-sm px-6 py-4 inline-flex items-center justify-center gap-2 transition-all hover:orange-glow"
            >
              <Lock size={14} />
              {checkingOut ? "Opening Secure Checkout..." : "SECURE CHECKOUT"}
            </button>
            <SaveCartForm items={items} subtotal={subtotal} />
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
